import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { catalogApi } from '../../services/api';
import type { ServiceCategory } from '../../types';

interface ExtraField {
  name: string;
  type: 'text' | 'number' | 'select' | 'date' | 'boolean';
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_vehicle_category: false
  });

  // Extra fields configuration
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [configuringCategory, setConfiguringCategory] = useState<ServiceCategory | null>(null);
  const [extraFields, setExtraFields] = useState<ExtraField[]>([]);
  const [newField, setNewField] = useState<ExtraField>({
    name: '',
    type: 'text',
    label: '',
    required: false,
    placeholder: '',
    options: []
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await catalogApi.getCategories({ ordering: 'name' });
      setCategories(data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar categorias');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Nome da categoria é obrigatório');
      return;
    }

    try {
      setError(null);

      // Preparar dados para enviar
      let dataToSend: any = { ...formData };

      // Se marcou como categoria de veículo E não tem schema de campos ainda
      // OU se está mudando para categoria de veículo
      if (formData.is_vehicle_category) {
        // Se está criando nova categoria ou editando e não tinha schema antes
        if (!editingCategory || !editingCategory.extra_fields_schema?.fields) {
          // Criar schema inicial com campos obrigatórios
          dataToSend = {
            ...dataToSend,
            extra_fields_schema: {
              fields: [
                {
                  name: 'placa',
                  type: 'text',
                  label: 'Placa do Veículo',
                  required: true,
                  placeholder: 'Ex: ABC-1234'
                },
                {
                  name: 'alias',
                  type: 'text',
                  label: 'Alias/Apelido',
                  required: true,
                  placeholder: 'Ex: Caminhão 01'
                }
              ]
            }
          };
        } else {
          // Se já tinha schema, garantir que os campos existam
          const existingFields = editingCategory.extra_fields_schema.fields || [];
          const hasPlaca = existingFields.some(f => f.name === 'placa');
          const hasAlias = existingFields.some(f => f.name === 'alias');

          const updatedFields = [...existingFields];

          if (!hasPlaca) {
            updatedFields.unshift({
              name: 'placa',
              type: 'text',
              label: 'Placa do Veículo',
              required: true,
              placeholder: 'Ex: ABC-1234'
            });
          }

          if (!hasAlias) {
            const insertIndex = hasPlaca ? 1 : 0;
            updatedFields.splice(insertIndex, 0, {
              name: 'alias',
              type: 'text',
              label: 'Alias/Apelido',
              required: true,
              placeholder: 'Ex: Caminhão 01'
            });
          }

          dataToSend = {
            ...dataToSend,
            extra_fields_schema: {
              fields: updatedFields
            }
          };
        }
      }

      if (editingCategory) {
        const updatedCategory = await catalogApi.updateCategory(editingCategory.id.toString(), dataToSend);
        setCategories(prev => prev.map(cat =>
          cat.id === editingCategory.id ? updatedCategory : cat
        ));
      } else {
        const newCategory = await catalogApi.createCategory(dataToSend);
        setCategories(prev => [...prev, newCategory]);
      }

      resetForm();
    } catch (err) {
      setError('Erro ao salvar categoria');
      console.error('Error saving category:', err);
    }
  };

  const handleEdit = (category: ServiceCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      is_active: category.is_active,
      is_vehicle_category: category.is_vehicle_category || false
    });
    setShowForm(true);
  };

  const handleDelete = async (category: ServiceCategory) => {
    if (window.confirm(`Tem certeza que deseja excluir a categoria "${category.name}"? Esta ação não pode ser desfeita.`)) {
      try {
        await catalogApi.deleteCategory(category.id.toString());
        setCategories(prev => prev.filter(cat => cat.id !== category.id));
      } catch (err) {
        setError('Erro ao excluir categoria');
        console.error('Error deleting category:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', is_active: true, is_vehicle_category: false });
    setEditingCategory(null);
    setShowForm(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Extra fields functions
  const handleConfigureFields = (category: ServiceCategory) => {
    setConfiguringCategory(category);

    // Load existing fields from extra_fields_schema
    if (category.extra_fields_schema && category.extra_fields_schema.fields) {
      setExtraFields(category.extra_fields_schema.fields);
    } else {
      setExtraFields([]);
    }

    setShowFieldsModal(true);
  };

  const handleAddField = () => {
    if (!newField.name || !newField.label) {
      setError('Nome e rótulo do campo são obrigatórios');
      return;
    }

    setExtraFields(prev => [...prev, { ...newField }]);
    setNewField({
      name: '',
      type: 'text',
      label: '',
      required: false,
      placeholder: '',
      options: []
    });
    setError(null);
  };

  const handleRemoveField = (index: number) => {
    setExtraFields(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveFields = async () => {
    if (!configuringCategory) return;

    try {
      console.log('Salvando campos:', extraFields);
      console.log('Categoria:', configuringCategory.id);

      // Se for categoria de veículo, garantir que campos obrigatórios existam
      let fieldsToSave = [...extraFields];

      if (configuringCategory.is_vehicle_category) {
        // Verificar se já existem os campos
        const hasPlaca = fieldsToSave.some(f => f.name === 'placa');
        const hasAlias = fieldsToSave.some(f => f.name === 'alias');

        // Adicionar placa se não existir
        if (!hasPlaca) {
          fieldsToSave.unshift({
            name: 'placa',
            type: 'text',
            label: 'Placa do Veículo',
            required: true,
            placeholder: 'Ex: ABC-1234'
          });
        }

        // Adicionar alias se não existir
        if (!hasAlias) {
          fieldsToSave.splice(1, 0, {
            name: 'alias',
            type: 'text',
            label: 'Alias/Apelido',
            required: true,
            placeholder: 'Ex: Caminhão 01'
          });
        }
      }

      const payload = {
        extra_fields_schema: {
          fields: fieldsToSave
        }
      };

      console.log('Payload:', JSON.stringify(payload, null, 2));

      const result = await catalogApi.updateCategory(
        configuringCategory.id.toString(),
        payload
      );

      console.log('Resultado:', result);

      // Recarregar categorias para garantir sincronização
      await loadCategories();

      setShowFieldsModal(false);
      setConfiguringCategory(null);
      setExtraFields([]);
      setError(null);
    } catch (err: any) {
      console.error('Erro completo:', err);
      console.error('Response:', err.response?.data);
      setError(`Erro ao salvar configuração de campos: ${err.response?.data?.detail || err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando categorias...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800">Gerenciar Categorias</h2>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Nova Categoria
            </button>
          </div>
        </div>

        {error && (
          <div className="px-6 py-4 bg-red-50 border-b border-red-200">
            <div className="flex">
              <ExclamationCircleIcon className="h-4 w-4 text-red-400 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Categoria *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex items-center justify-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Ativa</span>
                  </label>
                </div>
              </div>

              <div className="col-span-2">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_vehicle_category"
                      checked={formData.is_vehicle_category}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-900">Categoria de Veículo</span>
                  </label>
                  <p className="ml-6 mt-1 text-xs text-gray-600">
                    Ao marcar, os campos obrigatórios "Placa" e "Alias" serão automaticamente adicionados aos equipamentos desta categoria
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {editingCategory ? 'Salvar Alterações' : 'Criar Categoria'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Categories List */}
        <div className="p-6">
          {categories.length > 0 ? (
            <div className="space-y-4">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${category.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <TagIcon className={`h-5 w-5 ${category.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">{category.name}</h3>
                        {!category.is_active && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inativa
                          </span>
                        )}
                      </div>
                      {category.description && (
                        <p className="text-xs text-gray-600 mt-1">{category.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                        <span>ID: #{category.id}</span>
                        <span>Criada: {new Date(category.created_at).toLocaleDateString('pt-BR')}</span>
                        {category.services_count !== undefined && (
                          <span>{category.services_count} equipamentos</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleConfigureFields(category)}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500"
                      title="Configurar campos extras"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      title="Editar categoria"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDelete(category)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500"
                      title="Excluir categoria"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma categoria encontrada</h3>
              <p className="mt-1 text-xs text-gray-500">
                Comece criando uma nova categoria para organizar seus equipamentos.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  Nova Categoria
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Extra Fields Configuration Modal */}
      {showFieldsModal && configuringCategory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                Configurar Campos Extras - {configuringCategory.name}
              </h3>
              <button
                onClick={() => {
                  setShowFieldsModal(false);
                  setConfiguringCategory(null);
                  setExtraFields([]);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="mt-4 space-y-6">
              {/* Current Fields */}
              {extraFields.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Campos Configurados:</h4>
                  <div className="space-y-2">
                    {extraFields.map((field, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{field.label}</span>
                            <span className="text-xs text-gray-500">({field.name})</span>
                            {field.required && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                                Obrigatório
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                            <span>Tipo: {field.type}</span>
                            {field.placeholder && <span>Placeholder: {field.placeholder}</span>}
                            {field.options && field.options.length > 0 && (
                              <span>Opções: {field.options.join(', ')}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveField(index)}
                          className="ml-4 p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Field Form */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Adicionar Novo Campo:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Nome do Campo (identificador) *
                    </label>
                    <input
                      type="text"
                      value={newField.name}
                      onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                      placeholder="ex: placa, tipo_veiculo"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Rótulo (exibido no formulário) *
                    </label>
                    <input
                      type="text"
                      value={newField.label}
                      onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                      placeholder="ex: Placa do Veículo"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Tipo de Campo
                    </label>
                    <select
                      value={newField.type}
                      onChange={(e) =>
                        setNewField({
                          ...newField,
                          type: e.target.value as ExtraField['type']
                        })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="text">Texto</option>
                      <option value="number">Número</option>
                      <option value="select">Seleção</option>
                      <option value="date">Data</option>
                      <option value="boolean">Sim/Não</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Placeholder (opcional)
                    </label>
                    <input
                      type="text"
                      value={newField.placeholder || ''}
                      onChange={(e) =>
                        setNewField({ ...newField, placeholder: e.target.value })
                      }
                      placeholder="ex: ABC-1234"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {newField.type === 'select' && (
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Opções (separadas por vírgula)
                      </label>
                      <input
                        type="text"
                        value={newField.options?.join(', ') || ''}
                        onChange={(e) =>
                          setNewField({
                            ...newField,
                            options: e.target.value.split(',').map(opt => opt.trim())
                          })
                        }
                        placeholder="ex: Caminhão, Camionete, Van, Moto"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  )}

                  <div className="col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newField.required}
                        onChange={(e) =>
                          setNewField({ ...newField, required: e.target.checked })
                        }
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Campo obrigatório</span>
                    </label>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={handleAddField}
                    className="w-full px-3 py-1.5 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-medium"
                  >
                    <PlusIcon className="h-4 w-4 inline mr-2" />
                    Adicionar Campo
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="mt-6 flex items-center justify-end space-x-2 border-t pt-4">
              <button
                onClick={() => {
                  setShowFieldsModal(false);
                  setConfiguringCategory(null);
                  setExtraFields([]);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveFields}
                className="px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Salvar Configuração
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManager;